/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect } from 'react';
import { Input, message, Modal } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { styleFn } from '~components/molecules/RightSideNav/Modals/ModalFormStyles.style';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { useStyles } from '~src/hooks/useStyles';
import { editShare } from '~src/services/api/shares';
import { Controller, useForm } from 'react-hook-form';

export type EditShareTenderModalProps = Pick<ModalProps, 'visible'> & {
  onCancel?: () => void;
  projectId: number;
  tenderId: number;
  shareKey: string;
  emailDisabled?: boolean;
  previousData?: {
    email?: string;
    title?: string;
  };
};

export const EditShareTenderModal: React.FC<EditShareTenderModalProps> = ({
  onCancel,
  projectId,
  tenderId,
  shareKey,
  emailDisabled,
  previousData,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const sharesForm = useForm({
    defaultValues: {
      email: '',
      title: '',
    },
  });

  const [updateShareMutation, { isLoading }] = useMutation(editShare, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'questions-answering-failed' }));
      appLog('addAnswerMutation -> item', item);
      appLog('addAnswerMutation -> err', error);
    },
  });

  const handleResetForm = () => {
    sharesForm.reset();
  };

  const isValid = () => {
    const { email, title } = sharesForm.getValues();
    if (!email || !title) {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isValid()) return;

      await updateShareMutation({
        projectId,
        tenderId,
        shareKey,
        data: sharesForm.getValues(),
      });

      handleResetForm();

      onCancel?.();
    } catch (err) {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
    }
  };

  useEffect(() => {
    const values = sharesForm.getValues();
    if (previousData && !values.email) {
      sharesForm.reset({
        email: previousData.email,
        title: previousData.title,
      });
    }
  }, [previousData]);

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: 'edit',
          })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'edit' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{
        // disabled: !isValid(),
        loading: isLoading,
      }}
      onOk={handleSubmit}
      onCancel={() => {
        handleResetForm();
        onCancel?.();
      }}
      destroyOnClose
      zIndex={2000}
      width={500}
      {...modalProps}>
      <div css={styles.formContainer}>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'email' })}</p>
          <Controller
            name="email"
            control={sharesForm.control}
            render={({ field }) => (
              <Input disabled={emailDisabled} {...field} />
            )}
          />
        </div>
        <div css={styles.formItem}>
          <p css={styles.formLabel}>{intl.formatMessage({ id: 'title' })}</p>
          <Controller
            name="title"
            control={sharesForm.control}
            render={({ field }) => <Input {...field} />}
          />
        </div>
      </div>
    </Modal>
  );
};
